// IndividProp.js - Main Modern Component
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import Gallery from "./Gallery";
import AllInOne from "./AllInOne";
import Section from "./Section";
import FirstSection from "./Three_Sections_Data/FirstSection";
import SecondSection from "./Three_Sections_Data/SecondSection";
import ThirdSection from "./Three_Sections_Data/ThirdSection";
import Loader from "../Loader/Loader";


function IndividProp() {
  const { propertyId } = useParams();
  const [openSectionIndex, setOpenSectionIndex] = useState(0);
  const [propertyData, setPropertyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleSectionClick = (index) => {
    setOpenSectionIndex(index === openSectionIndex ? null : index);
  };

  useEffect(() => {
    const fetchPropertyData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`https://app.akiya2.com/one_property/${propertyId}`);
        setPropertyData(response.data);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load property data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    if (propertyId) {
      fetchPropertyData();
    }
  }, [propertyId]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center">
        <div className="text-center space-y-6">
          <div className="relative">
            <Loader big={true} />
            <div className="absolute inset-0 bg-[#10c9c0]/10 rounded-full blur-3xl animate-pulse"></div>
          </div>
          <div className="space-y-2">
            <p className="text-xl font-semibold text-gray-700">Loading property details...</p>
            <p className="text-gray-500">Please wait while we fetch the information</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center">
        <div className="text-center space-y-6 max-w-md mx-auto px-4">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-gray-900">Oops! Something went wrong</h2>
            <p className="text-gray-600">{error}</p>
          </div>
          <Link
            to="/our-projects"
            className="inline-flex items-center px-6 py-3 bg-[#10c9c0] text-white rounded-xl font-semibold hover:bg-[#0ea5a0] transition-colors duration-300"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Properties
          </Link>
        </div>
      </div>
    );
  }

  // No data state
  if (!propertyData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center">
        <div className="text-center space-y-6 max-w-md mx-auto px-4">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h3M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-gray-900">Property Not Found</h2>
            <p className="text-gray-600">The property you're looking for doesn't exist or has been removed.</p>
          </div>
          <Link
            to="/our-projects"
            className="inline-flex items-center px-6 py-3 bg-[#10c9c0] text-white rounded-xl font-semibold hover:bg-[#0ea5a0] transition-colors duration-300"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Browse All Properties
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-[#10c9c0]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-[#10c9c0]/5 rounded-full blur-3xl"></div>
        <div className="absolute top-3/4 left-1/4 w-64 h-64 bg-[#10c9c0]/3 rounded-full blur-3xl"></div>
      </div>

      <div className="relative flex justify-center">
        <div className="w-full max-w-[1440px] px-4 py-8">
          
          {/* Gallery Section */}
          <section className="mb-8">
            <Gallery images={propertyData.images} video={propertyData.video} />
          </section>

          {/* Property Header */}
          <section className="mb-8">
            <AllInOne property={propertyData} />
          </section>

          {/* Navigation breadcrumb */}
          <div className="flex items-center space-x-2 text-sm text-gray-500 mb-8 justify-center">
            <Link to="/our-projects" className="hover:text-[#10c9c0] transition-colors">
              Properties
            </Link>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-gray-700 font-medium truncate max-w-xs">
              {propertyData.propertyName}
            </span>
          </div>

          {/* Accordion Sections */}
          <div className="space-y-4">
            <Section
              header="Maps and Access"
              data={<FirstSection data={propertyData} />}
              isOpen={openSectionIndex === 0}
              onClick={() => handleSectionClick(0)}
            />
            
            <Section
              header="Amenities"
              data={<SecondSection data={propertyData} />}
              isOpen={openSectionIndex === 1}
              onClick={() => handleSectionClick(1)}
            />
            
            <Section
              header="Blogs"
              data={<ThirdSection />}
              isOpen={openSectionIndex === 2}
              onClick={() => handleSectionClick(2)}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full mt-12 mb-8">
            {/* Back Button */}
            <Link to="/our-projects">
              <button className="group relative overflow-hidden bg-gradient-to-r from-[#10c9c0] to-[#0ea5a0] rounded-xl font-bold text-white px-8 py-4 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                <div className="flex items-center space-x-3">
                  <svg className="w-5 h-5 transition-transform duration-300 group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  <span>Back to Properties</span>
                </div>
                
                {/* Hover effect */}
                <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
              </button>
            </Link>

            {/* Contact/Inquiry Button */}
            <Link to={`/about-us/#form`}>


            <button to={`/about-us/#form`} className="group relative overflow-hidden bg-white border-2 border-[#10c9c0] rounded-xl font-bold text-[#10c9c0] px-8 py-4 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 hover:bg-[#10c9c0] hover:text-white">
              <div className="flex items-center space-x-3">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <span>Contact Us</span>
              </div>
            </button>
            </Link>
          </div>

          {/* Footer info */}
          <div className="text-center text-gray-500 text-sm py-8 border-t border-gray-200">
            <p>Property ID: {propertyId} • Last updated: {new Date().toLocaleDateString()}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default IndividProp;